import React, { ReactElement, useState } from "react";
import { Button, makeStyles, useTheme } from "@material-ui/core";

import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Fade from "@mui/material/Fade";

import MoreVertIcon from "@mui/icons-material/MoreVert";

import ModalCancelScheduling from "../modal/ModalCancelScheduling";
import ModalSchedule from "../modal/ModalSchedule";

import { TypographyCaption } from "../typography";
import { IScheduledLoad, StatusSchedule } from "../../model";
import { KEY, STATUSROADLOAD, STATUSTYPE } from "@utils/enum";
import { useFlags } from "flagsmith/react";
import { DeliveryType } from "@utils/DeliveryType";

interface Props<T> {
  row: T;
  rowIndex: number;
  rescheduleAllowed: boolean;
  handleCancelScheduling: (scheduleGroupID: string) => Promise<void>;
  handleCancelCarCharging: (rowData: T) => Promise<void>;
  handleEditAndCreateCarCharging: (rowData: T) => Promise<void>;
  handleEditDriverAndPlate: (rowData: T) => Promise<void>;
  cancelSchedulingAllowed: boolean;
  scheduleCarCharging: boolean;
  cancelCarCharging: boolean;
  editCarCharging: boolean;
  editDriverAllowed?: boolean;
  showEditDriverInMenu?: boolean;
  status: STATUSROADLOAD;
  documentationPending?: boolean;
  onClickDocumentsPending?: () => void;
}

type MenuOption = {
  allowed?: boolean;
  onComponent: ReactElement;
};

const LongMenu = <T,>(props: Props<T>): ReactElement => {
  const {
    row,
    rowIndex,
    rescheduleAllowed,
    handleCancelScheduling,
    handleCancelCarCharging,
    handleEditAndCreateCarCharging,
    handleEditDriverAndPlate,
    scheduleCarCharging,
    editCarCharging,
    cancelCarCharging,
    cancelSchedulingAllowed,
    editDriverAllowed,
    documentationPending,
    onClickDocumentsPending,
  } = props;

  const theme = useTheme();
  const classes = useStyles();

  const featureFlagProgrammingTransport = useFlags([
    "user_sidebar_programming_transport",
  ]).user_sidebar_programming_transport.enabled;

  const isFeatureFlagTransportEnabled = featureFlagProgrammingTransport;
  const isFOB = (row as { freight?: string }).freight === DeliveryType.FOB;

  const statusProgramation = row?.[KEY.STATUS_PROGRAMATION];

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const options: MenuOption[] = [];

  switch (statusProgramation) {
    case StatusSchedule.PROGRAMMED:
    case StatusSchedule.PARTIALLY_PROGRAMMED:
      options.push({
        allowed: documentationPending,
        onComponent: (
          <span>
            <Button
              onClick={() => {
                handleClose();
                onClickDocumentsPending?.();
              }}
              className={classes.itemMenu}
            >
              <TypographyCaption
                sx={{ color: theme.palette.text.primary, ml: "0px" }}
              >
                Documentos pendentes
              </TypographyCaption>
            </Button>
          </span>
        ),
      });
    //NOTE: Fall through para adicionar opções comuns a PROGRAMMED/PARTIALLY_PROGRAMMED e PENDING
    case StatusSchedule.PENDING:
      options.push(
        {
          allowed: rescheduleAllowed,
          onComponent: (
            <ModalSchedule
              key={rowIndex}
              roadLoadScheduled={row as IScheduledLoad}
              isDisable={!row?.[KEY.RESCHEDULABLE]}
              hiddenIcon
            >
              <TypographyCaption
                sx={{ color: theme.palette.text.primary, ml: "-8px" }}
              >
                Reagendar
              </TypographyCaption>
            </ModalSchedule>
          ),
        },
        {
          allowed: cancelSchedulingAllowed,
          onComponent: (
            <ModalCancelScheduling
              isDisabled={row[KEY.SALES_ORDER]?.some(
                (item) => item.status === STATUSTYPE.FATURADO
              )}
              scheduleID={row[KEY.REF]}
              handleCancelScheduling={() =>
                handleCancelScheduling(row[KEY.SCHEDULE_GROUP_ID])
              }
              hiddenIcon
            >
              <TypographyCaption
                sx={{ color: theme.palette.text.primary, ml: "-2px" }}
              >
                Cancelar Agendamento
              </TypographyCaption>
            </ModalCancelScheduling>
          ),
        },
        {
          allowed: editDriverAllowed,
          onComponent: (
            <span>
              <Button
                disabled={false}
                onClick={() => handleEditDriverAndPlate(row)}
                className={classes.itemMenu}
              >
                <TypographyCaption
                  sx={{ color: theme.palette.text.primary, ml: "0px" }}
                >
                  Editar Motorista/Placa
                </TypographyCaption>
              </Button>
            </span>
          ),
        }
      );
      break;
    default:
      break;
  }

  if (isFeatureFlagTransportEnabled && isFOB) {
    switch (statusProgramation) {
      case StatusSchedule.PENDING:
        options.push({
          allowed: scheduleCarCharging,
          onComponent: (
            <span>
              <Button
                disabled={false}
                onClick={() => handleEditAndCreateCarCharging(row)}
                className={classes.itemMenu}
              >
                <TypographyCaption
                  sx={{ color: theme.palette.text.primary, ml: "0px" }}
                >
                  Programar Carregamento
                </TypographyCaption>
              </Button>
            </span>
          ),
        });
        break;
      case StatusSchedule.PROGRAMMED:
      case StatusSchedule.PARTIALLY_PROGRAMMED:
        options.push(
          {
            allowed: editCarCharging,
            onComponent: (
              <span>
                <Button
                  disabled={false}
                  onClick={() => handleEditAndCreateCarCharging(row)}
                  className={classes.itemMenu}
                >
                  <TypographyCaption
                    sx={{ color: theme.palette.text.primary, ml: "0px" }}
                  >
                    Editar Carregamento
                  </TypographyCaption>
                </Button>
              </span>
            ),
          },
          {
            allowed: cancelCarCharging,
            onComponent: (
              <span>
                <Button
                  disabled={false}
                  onClick={() => {
                    handleCancelCarCharging(row);
                    handleClose();
                  }}
                  className={classes.itemMenu}
                >
                  <TypographyCaption
                    sx={{ color: theme.palette.text.primary, ml: "0px" }}
                  >
                    Cancelar Programação
                  </TypographyCaption>
                </Button>
              </span>
            ),
          }
        );
        break;
    }
  }

  if (!options.length) {
    return (
      <IconButton
        disabled
        sx={{
          visibility: "hidden",
          pointerEvents: "none",
          width: 40,
          height: 40,
        }}
      >
        <MoreVertIcon />
      </IconButton>
    );
  }

  return (
    <div>
      <IconButton
        aria-label="more"
        id="long-button"
        aria-controls={open ? "long-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={handleClick}
        sx={{
          color: theme.palette.primary.main,
        }}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="long-menu"
        MenuListProps={{ "aria-labelledby": "long-button" }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        sx={{
          "& .MuiMenu-list": {
            border: `1px solid ${theme.palette.background.default}`,
            borderRadius: 2,
          },
        }}
      >
        {options.map((option, index) => (
          <React.Fragment key={index}>
            <MenuItem
              disabled={!option.allowed}
              className={`menu-item-${index}`}
            >
              {option.onComponent}
            </MenuItem>
          </React.Fragment>
        ))}
      </Menu>
    </div>
  );
};

export default LongMenu;

const useStyles = makeStyles({
  itemMenu: {
    p: { xs: "4px", md: "8px" },
    gap: 1,
    borderRadius: 0,
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
});
